import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Col, Input, notification, Radio, Row, Select, Spin } from "antd";
import NumberFormat from "react-number-format";

import ButtonStandard from "../../components/utils/button";
import PencilIcon from "../../components/utils/icons/pencil";
import { EditButton } from "../reproductionParameters/cards/styles";
import { CardCustom } from "./styles";
import {
  findFeedFactoryParameterData,
  saveFeedFactoryParameterData,
} from "../../services/feedFactoryParameterService";
import { index as indexWagons } from "../../services/wagonService";

const INITIAL_FORM_VALUES = {
  useFullMixerCapacity: true,
  standardCostPerBatch: null,
  defaultWagonId: null,
};

function FeedFactoryParameters() {
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [form, setForm] = useState(INITIAL_FORM_VALUES);
  const [wagons, setWagons] = useState([]);

  const {
    translation,
    groupSelected: { id: groupId },
    farmSelected: { id: farmId },
  } = useSelector((state) => state.app);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [
        {
          data: { results },
        },
        {
          data: { results: wagonResults },
        },
      ] = await Promise.all([
        findFeedFactoryParameterData({ groupId, farmId }),
        indexWagons({ groupId, farmId, size: 1000 }),
      ]);
      setForm(
        results ? { ...INITIAL_FORM_VALUES, ...results } : INITIAL_FORM_VALUES,
      );
      setWagons(
        (wagonResults?.content ?? wagonResults ?? []).filter(
          (wagon) => wagon.status === "Active",
        ),
      );
    } catch (error) {
      notification.error({
        message: translation.feedFactoryParameters.errorLoad,
      });
    } finally {
      setIsLoading(false);
    }
  }, [farmId, groupId, translation]);

  const handleCancel = useCallback(() => {
    setIsEditing(false);
    fetchData();
  }, [fetchData]);

  const handleSubmit = useCallback(async () => {
    setIsLoading(true);
    try {
      await saveFeedFactoryParameterData({
        groupId,
        farmId,
        body: {
          useFullMixerCapacity: form.useFullMixerCapacity,
          standardCostPerBatch: form.standardCostPerBatch,
          defaultWagonId: form.defaultWagonId,
        },
      });
      notification.success({
        message: translation.feedFactoryParameters.successSave,
      });
      setIsEditing(false);
      fetchData();
    } catch (error) {
      notification.error({
        message: translation.feedFactoryParameters.errorSave,
      });
    } finally {
      setIsLoading(false);
    }
  }, [farmId, fetchData, form, groupId, translation]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <CardCustom>
      <Spin spinning={isLoading}>
        <div className="body-container">
          <Row type="flex" justify="end" gutter={8}>
            {isEditing ? (
              <>
                <Col>
                  <ButtonStandard
                    type="button"
                    name="buttonSaveFeedFactoryParameters"
                    buttonType="principal"
                    onClick={handleSubmit}
                  >
                    {translation.buttons.save}
                  </ButtonStandard>
                </Col>
                <Col>
                  <ButtonStandard
                    type="button"
                    name="buttonCancelFeedFactoryParameters"
                    buttonType="secondary"
                    onClick={handleCancel}
                  >
                    {translation.buttons.cancel}
                  </ButtonStandard>
                </Col>
              </>
            ) : (
              <Col>
                <EditButton
                  type="button"
                  onClick={() => setIsEditing(true)}
                  disabled={isEditing}
                >
                  {translation.buttons.edit}
                  <PencilIcon />
                </EditButton>
              </Col>
            )}
          </Row>

          <Row type="flex" gutter={32}>
            <Col xs={24} sm={24} md={12} lg={8} xl={8}>
              <Row>
                <label>
                  {translation.feedFactoryParameters.form.useFullMixerCapacity}
                </label>
              </Row>
              <Row type="flex" gutter={8} align="middle">
                <Col>
                  <Radio
                    disabled={!isEditing}
                    checked={form.useFullMixerCapacity === true}
                    onChange={() =>
                      setForm((prev) => ({
                        ...prev,
                        useFullMixerCapacity: true,
                      }))
                    }
                  >
                    {translation.buttons.yes}
                  </Radio>
                </Col>
                <Col>
                  <Radio
                    disabled={!isEditing}
                    checked={form.useFullMixerCapacity === false}
                    onChange={() =>
                      setForm((prev) => ({
                        ...prev,
                        useFullMixerCapacity: false,
                      }))
                    }
                  >
                    {translation.buttons.no}
                  </Radio>
                </Col>
              </Row>
            </Col>

            <Col xs={24} sm={24} md={12} lg={8} xl={8}>
              <Row>
                <label>
                  {translation.feedFactoryParameters.form.defaultWagon}
                </label>
              </Row>
              <Row>
                <Select
                  style={{ width: "100%" }}
                  disabled={!isEditing}
                  allowClear
                  value={form.defaultWagonId || undefined}
                  placeholder={translation.defaultSelectPlaceholder}
                  onChange={(value) =>
                    setForm((prev) => ({
                      ...prev,
                      defaultWagonId: value ?? null,
                    }))
                  }
                >
                  {wagons.map((wagon) => (
                    <Select.Option key={wagon.id} value={wagon.id}>
                      {wagon.name}
                    </Select.Option>
                  ))}
                </Select>
              </Row>
            </Col>
          </Row>

          <Row type="flex" gutter={32}>
            <Col xs={24} sm={24} md={12} lg={8} xl={8}>
              <Row>
                <label>
                  {translation.feedFactoryParameters.form.standardCostPerBatch}
                </label>
              </Row>
              <Row>
                <NumberFormat
                  customInput={Input}
                  value={form.standardCostPerBatch}
                  disabled={!isEditing}
                  addonBefore="R$"
                  addonAfter={translation.feedFactoryParameters.form.perBatch}
                  decimalScale={2}
                  decimalSeparator=","
                  thousandSeparator="."
                  fixedDecimalScale={true}
                  allowNegative={false}
                  onValueChange={({ floatValue }) =>
                    setForm((prev) => ({
                      ...prev,
                      standardCostPerBatch:
                        floatValue == null ? null : floatValue,
                    }))
                  }
                />
              </Row>
            </Col>
          </Row>
        </div>
      </Spin>
    </CardCustom>
  );
}

export default FeedFactoryParameters;
